import {
  Box,
  Button,
  Checkbox,
  Dialog,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { X as CloseIcon, MoveVertical, Plus, Trash2, TriangleAlert } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAnalysisStore } from '../../stores/analysisStore';
import {
  DATA_TYPE_META,
  resolveTraceColor,
  TRACE_COLOR_PALETTE,
  useGraphStore,
} from '../../stores/graphStore';
import { useNavigationStore } from '../../stores/navigationStore';

const X_UNIT_OPTIONS = ['hh:mm', 'hh:mm:ss', 'seconds'];

function formatTimeByUnit(seconds, unit) {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return '';
  const s = Number(seconds);
  if (unit === 'seconds') return String(Math.round(s));
  const totalSec = Math.max(0, Math.floor(s));
  const hh = String(Math.floor(totalSec / 3600)).padStart(2, '0');
  const mm = String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0');
  const ss = String(totalSec % 60).padStart(2, '0');
  if (unit === 'hh:mm') return `${hh}:${mm}`;
  return `${hh}:${mm}:${ss}`;
}

function parseTimeByUnit(text, unit) {
  if (!text && text !== 0) return null;
  const trimmed = String(text).trim();
  if (trimmed === '') return null;
  if (unit === 'seconds') {
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  }
  const parts = trimmed.split(':').map((p) => p.trim());
  if (parts.some((p) => p === '' || !/^\d+$/.test(p))) return null;
  const [h = 0, m = 0, s = 0] = parts.map(Number);
  if (unit === 'hh:mm') return h * 3600 + m * 60;
  return h * 3600 + m * 60 + s;
}

// ─────────────────────────────────────────────────────────────────────

function ScalingTab({ graph, onScalingChange, onTimeAxisCommit, onFitYAxis, dataExtentSec }) {
  const { traces, scaling } = graph;

  const y1Trace = traces.find((t) => t.yAxis === 'y1');
  const y2Trace = traces.find((t) => t.yAxis === 'y2');
  const y1Label = y1Trace
    ? `${DATA_TYPE_META[y1Trace.channel]?.label ?? y1Trace.channel} [${DATA_TYPE_META[y1Trace.channel]?.unit ?? ''}]`
    : '—';
  const y2Label = y2Trace
    ? `${DATA_TYPE_META[y2Trace.channel]?.label ?? y2Trace.channel} [${DATA_TYPE_META[y2Trace.channel]?.unit ?? ''}]`
    : '—';
  const y1Color = y1Trace ? resolveTraceColor(y1Trace.color) : '#A9B6BC';
  const y2Color = y2Trace ? resolveTraceColor(y2Trace.color) : '#A9B6BC';
  const hasY1 = !!y1Trace;
  const hasY2 = !!y2Trace;

  const setNumeric = (key) => (ev) => {
    const value = ev.target.value === '' ? null : Number(ev.target.value);
    onScalingChange({ [key]: Number.isFinite(value) ? value : null });
  };

  // Y-axis Range field: keeps Min fixed and sets Max = Min + Range
  // (Min defaults to 0 if not yet set).
  const setRange = (minKey, maxKey) => (ev) => {
    const range = ev.target.value === '' ? null : Number(ev.target.value);
    if (!Number.isFinite(range) || range <= 0) {
      onScalingChange({ [maxKey]: null });
      return;
    }
    const currentMin = scaling[minKey];
    const baseMin = Number.isFinite(currentMin) ? currentMin : 0;
    onScalingChange({ [minKey]: baseMin, [maxKey]: baseMin + range });
  };

  const rangeFor = (min, max) => {
    if (min === null || max === null) return '';
    return String(Number((max - min).toFixed(4)));
  };

  // Time-axis text stays local; commits on blur or Enter.
  const xMinDisplay = formatTimeByUnit(scaling.xMin, scaling.xUnit);
  const xMaxDisplay = formatTimeByUnit(scaling.xMax, scaling.xUnit);
  const xRangeDisplay =
    scaling.xMin !== null && scaling.xMax !== null
      ? formatTimeByUnit(scaling.xMax - scaling.xMin, scaling.xUnit)
      : '';

  const [minText, setMinText] = useState(xMinDisplay);
  const [maxText, setMaxText] = useState(xMaxDisplay);
  const [rangeText, setRangeText] = useState(xRangeDisplay);

  // Re-seed local text on store-side xMin/xMax/xUnit changes (commit or
  // reopen). Other store changes (y-axis edits, etc.) don't clobber typing.
  useEffect(() => {
    setMinText(xMinDisplay);
    setMaxText(xMaxDisplay);
    setRangeText(xRangeDisplay);
  }, [scaling.xMin, scaling.xMax, scaling.xUnit]);

  const clampToData = (sec) => {
    if (!dataExtentSec) return sec;
    const [dMin, dMax] = dataExtentSec;
    return Math.max(dMin, Math.min(dMax, sec));
  };

  const commit = (field) => {
    const parsedMin = parseTimeByUnit(minText, scaling.xUnit);
    const parsedMax = parseTimeByUnit(maxText, scaling.xUnit);
    const parsedRange = parseTimeByUnit(rangeText, scaling.xUnit);

    let newMin = scaling.xMin;
    let newMax = scaling.xMax;

    if (field === 'min') {
      if (parsedMin === null) {
        setMinText(xMinDisplay);
        return;
      }
      const prevRange = (scaling.xMax ?? 0) - (scaling.xMin ?? 0);
      newMin = clampToData(parsedMin);
      newMax = clampToData(newMin + (prevRange > 0 ? prevRange : 0));
    } else if (field === 'max') {
      if (parsedMax === null) {
        setMaxText(xMaxDisplay);
        return;
      }
      const prevRange = (scaling.xMax ?? 0) - (scaling.xMin ?? 0);
      newMax = clampToData(parsedMax);
      newMin = clampToData(newMax - (prevRange > 0 ? prevRange : 0));
    } else if (field === 'range') {
      if (parsedRange === null || parsedRange <= 0) {
        setRangeText(xRangeDisplay);
        return;
      }
      newMin = scaling.xMin ?? 0;
      newMax = clampToData(newMin + parsedRange);
      // If max got clamped to data end, pull min along too to preserve range
      if (newMax - newMin < parsedRange) {
        newMin = clampToData(newMax - parsedRange);
      }
    }

    if (newMax <= newMin) {
      // Invalid result — revert
      setMinText(xMinDisplay);
      setMaxText(xMaxDisplay);
      setRangeText(xRangeDisplay);
      return;
    }

    onTimeAxisCommit({ xMin: newMin, xMax: newMax });
  };

  const handleKeyDown = (field) => (ev) => {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      commit(field);
      ev.target.blur?.();
    } else if (ev.key === 'Escape') {
      // Revert local text to current store value
      setMinText(xMinDisplay);
      setMaxText(xMaxDisplay);
      setRangeText(xRangeDisplay);
      ev.target.blur?.();
    }
  };

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Box sx={{ border: '1px solid #dde3e6', borderRadius: 2, p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ width: 170 }}>
            <Typography fontWeight={600}>Time axis</Typography>
            <Typography variant="caption" sx={{ color: '#7d8d97' }}>
              time / [{scaling.xUnit}]
            </Typography>
          </Box>
          <TextField
            size="small"
            label="Range"
            value={rangeText}
            onChange={(ev) => setRangeText(ev.target.value)}
            onBlur={() => commit('range')}
            onKeyDown={handleKeyDown('range')}
            sx={{ width: 110 }}
          />
          <TextField
            size="small"
            label="Min"
            value={minText}
            onChange={(ev) => setMinText(ev.target.value)}
            onBlur={() => commit('min')}
            onKeyDown={handleKeyDown('min')}
            sx={{ width: 110 }}
          />
          <TextField
            size="small"
            label="Max"
            value={maxText}
            onChange={(ev) => setMaxText(ev.target.value)}
            onBlur={() => commit('max')}
            onKeyDown={handleKeyDown('max')}
            sx={{ width: 110 }}
          />
          <TextField
            size="small"
            select
            label="Unit"
            value={scaling.xUnit}
            onChange={(ev) => onScalingChange({ xUnit: ev.target.value })}
            sx={{ width: 130 }}
          >
            {X_UNIT_OPTIONS.map((u) => (
              <MenuItem key={u} value={u}>
                {u}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Box>

      <Box sx={{ border: `1px solid ${y1Color}`, borderRadius: 2, p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ width: 170 }}>
            <Typography fontWeight={600} sx={{ color: y1Color }}>
              y₁ axis (left)
            </Typography>
            <Typography variant="caption" sx={{ color: y1Color }}>
              {y1Label}
            </Typography>
          </Box>
          <TextField
            size="small"
            label="Range"
            type="number"
            value={rangeFor(scaling.y1Min, scaling.y1Max)}
            onChange={setRange('y1Min', 'y1Max')}
            sx={{ width: 130 }}
          />
          <TextField
            size="small"
            label="Min"
            type="number"
            value={scaling.y1Min ?? ''}
            onChange={setNumeric('y1Min')}
            sx={{ width: 130 }}
          />
          <TextField
            size="small"
            label="Max"
            type="number"
            value={scaling.y1Max ?? ''}
            onChange={setNumeric('y1Max')}
            sx={{ width: 130 }}
          />
          <Box sx={{ flex: 1 }} />
          <Tooltip title={hasY1 ? 'Fit to data' : 'No trace on y₁'}>
            <span>
              <IconButton
                size="small"
                onClick={() => onFitYAxis?.('y1')}
                disabled={!hasY1}
                aria-label="Fit y₁ axis to data"
                sx={{ color: y1Color }}
              >
                <MoveVertical size={18} />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      <Box sx={{ border: `1px solid ${y2Color}`, borderRadius: 2, p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ width: 170 }}>
            <Typography fontWeight={600} sx={{ color: y2Color }}>
              y₂ axis (right)
            </Typography>
            <Typography variant="caption" sx={{ color: y2Color }}>
              {y2Label}
            </Typography>
          </Box>
          <TextField
            size="small"
            label="Range"
            type="number"
            value={rangeFor(scaling.y2Min, scaling.y2Max)}
            onChange={setRange('y2Min', 'y2Max')}
            sx={{ width: 130 }}
          />
          <TextField
            size="small"
            label="Min"
            type="number"
            value={scaling.y2Min ?? ''}
            onChange={setNumeric('y2Min')}
            sx={{ width: 130 }}
          />
          <TextField
            size="small"
            label="Max"
            type="number"
            value={scaling.y2Max ?? ''}
            onChange={setNumeric('y2Max')}
            sx={{ width: 130 }}
          />
          <Box sx={{ flex: 1 }} />
          <Tooltip title={hasY2 ? 'Fit to data' : 'No trace on y₂'}>
            <span>
              <IconButton
                size="small"
                onClick={() => onFitYAxis?.('y2')}
                disabled={!hasY2}
                aria-label="Fit y₂ axis to data"
                sx={{ color: y2Color }}
              >
                <MoveVertical size={18} />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>
    </Stack>
  );
}

// ─────────────────────────────────────────────────────────────────────

function unitOfTrace(trace) {
  return DATA_TYPE_META[trace.channel]?.unit ?? '';
}

function PlotsTab({
  graph,
  onTraceChange,
  onTraceAdd,
  onTraceRemove,
  availableSignals,
  availableChambers,
}) {
  const firstUnitOnAxis = (axis) => {
    const first = graph.traces.find((t) => t.yAxis === axis);
    return first ? unitOfTrace(first) : null;
  };

  const handleAdd = () => {
    const usedChannels = new Set(graph.traces.map((t) => t.channel));
    const firstUnused =
      availableSignals.find((s) => !usedChannels.has(s.channel)) ?? availableSignals[0];
    if (!firstUnused) return;
    // Prefer the channel's defaultAxis. If that axis already hosts the same
    // channel, fall back to whichever axis has fewer traces.
    const channelMeta = DATA_TYPE_META[firstUnused.channel];
    const preferredAxis = channelMeta?.defaultAxis ?? 'y1';
    const sameChannelOnPreferred = graph.traces.some(
      (t) => t.yAxis === preferredAxis && t.channel === firstUnused.channel,
    );
    let yAxis = preferredAxis;
    if (sameChannelOnPreferred) {
      const y1Count = graph.traces.filter((t) => t.yAxis === 'y1').length;
      const y2Count = graph.traces.filter((t) => t.yAxis === 'y2').length;
      yAxis = y1Count <= y2Count ? 'y1' : 'y2';
    }
    const nextColor =
      TRACE_COLOR_PALETTE.find((c) => !graph.traces.some((t) => t.color === c.value))?.value ??
      channelMeta?.defaultColor ??
      TRACE_COLOR_PALETTE[0].value;
    onTraceAdd({
      channel: firstUnused.channel,
      chamberIndex: firstUnused.chamberIndex ?? null,
      color: nextColor,
      yAxis,
    });
  };

  return (
    <Stack spacing={1.5} sx={{ p: 2 }}>
      <Box>
        <Button variant="outlined" size="small" startIcon={<Plus size={14} />} onClick={handleAdd}>
          Add plot
        </Button>
      </Box>

      {graph.traces.length === 0 && (
        <Typography variant="body2" sx={{ color: '#7d8d97', fontStyle: 'italic', pl: 1 }}>
          No traces yet. Click "Add plot" to add one.
        </Typography>
      )}

      {graph.traces.map((trace) => {
        const axisUnit = firstUnitOnAxis(trace.yAxis);
        const traceUnit = unitOfTrace(trace);
        const axisTraces = graph.traces.filter((t) => t.yAxis === trace.yAxis);
        const mismatch = axisUnit && axisTraces[0]?.id !== trace.id && axisUnit !== traceUnit;

        const chambersForChannel = availableChambers.filter((ch) =>
          availableSignals.some(
            (s) => s.channel === trace.channel && s.chamberIndex === ch.chamberIndex,
          ),
        );

        return (
          <Box key={trace.id} sx={{ border: '1px solid #dde3e6', borderRadius: 2, p: 1.5 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <TextField
                select
                size="small"
                label="Channel"
                value={trace.channel}
                onChange={(ev) => onTraceChange(trace.id, { channel: ev.target.value })}
                sx={{ width: 180 }}
              >
                {availableSignals
                  .filter((s, idx, arr) => arr.findIndex((x) => x.channel === s.channel) === idx)
                  .map((s) => (
                    <MenuItem key={s.channel} value={s.channel}>
                      {DATA_TYPE_META[s.channel]?.label ?? s.channel}
                    </MenuItem>
                  ))}
              </TextField>

              {chambersForChannel.length > 1 && (
                <TextField
                  select
                  size="small"
                  label="Source"
                  value={trace.chamberIndex ?? ''}
                  onChange={(ev) =>
                    onTraceChange(trace.id, {
                      chamberIndex: ev.target.value === '' ? null : Number(ev.target.value),
                    })
                  }
                  sx={{ width: 140 }}
                >
                  {chambersForChannel.map((ch) => (
                    <MenuItem key={ch.chamberIndex} value={ch.chamberIndex}>
                      Chamber {ch.chamberIndex}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              <TextField
                select
                size="small"
                label="Color"
                value={trace.color}
                onChange={(ev) => onTraceChange(trace.id, { color: ev.target.value })}
                sx={{ width: 130 }}
              >
                {TRACE_COLOR_PALETTE.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: c.hex }} />
                      <span>{c.label}</span>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label="Y axis"
                value={trace.yAxis}
                onChange={(ev) => onTraceChange(trace.id, { yAxis: ev.target.value })}
                sx={{ width: 140 }}
              >
                <MenuItem value="y1">y₁ (left)</MenuItem>
                <MenuItem value="y2">y₂ (right)</MenuItem>
              </TextField>

              {mismatch && (
                <Tooltip title={`Unit mismatch with axis: ${axisUnit} vs ${traceUnit}`}>
                  <TriangleAlert size={16} color="#c98a00" />
                </Tooltip>
              )}

              <Box sx={{ flex: 1 }} />
              <IconButton
                size="small"
                onClick={() => onTraceRemove(trace.id)}
                aria-label="Delete plot"
              >
                <Trash2 size={16} />
              </IconButton>
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}

// ─────────────────────────────────────────────────────────────────────

function AdvancedTab({ graph, onAdvancedChange }) {
  const { advanced } = graph;
  return (
    <Stack spacing={1} sx={{ p: 2 }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={advanced.showMarks}
            onChange={(ev) => onAdvancedChange({ showMarks: ev.target.checked })}
          />
        }
        label="Show marks"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={advanced.showEvents}
            onChange={(ev) => onAdvancedChange({ showEvents: ev.target.checked })}
          />
        }
        label="Show events"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={advanced.showInstrumentAndNotificationEvents}
            onChange={(ev) =>
              onAdvancedChange({ showInstrumentAndNotificationEvents: ev.target.checked })
            }
          />
        }
        label="Show instrument and notification events"
      />
    </Stack>
  );
}

// ─────────────────────────────────────────────────────────────────────

export default function GraphOptionsDialog({ open, tabId, initialGraphId, onClose }) {
  const tab = useAnalysisStore((state) => state.openFiles[tabId]);
  const storeGraphs = useGraphStore((state) => state.tabGraphs[tabId]) || [];
  const applyGraphDraft = useGraphStore((state) => state.applyGraphDraft);
  const setTimeAxisForAllGraphs = useGraphStore((state) => state.setTimeAxisForAllGraphs);

  const [draft, setDraft] = useState(() => storeGraphs);
  const [activeGraphId, setActiveGraphId] = useState(initialGraphId ?? storeGraphs[0]?.id ?? null);
  const [activeTab, setActiveTab] = useState(0);

  // Re-seed draft on open. ChamberPlotView writes the current visible window
  // to navigationStore.tabVisibleWindows on pan/zoom end; we inject that into
  // the draft so the Scaling tab shows the latest crop without ever writing
  // to graph.scaling during pan/zoom (which would refire the plot's seed).
  useEffect(() => {
    if (!open) return;
    const current = useGraphStore.getState().tabGraphs[tabId] || [];
    const visible = tabId ? useNavigationStore.getState().tabVisibleWindows[tabId] : null;
    const seeded = current.map((g) => {
      if (!visible || !Number.isFinite(visible.xMinSec) || !Number.isFinite(visible.xMaxSec)) {
        return g;
      }
      return {
        ...g,
        scaling: { ...g.scaling, xMin: visible.xMinSec, xMax: visible.xMaxSec },
      };
    });
    setDraft(seeded);
    setActiveGraphId(initialGraphId ?? current[0]?.id ?? null);
    setActiveTab(0);
  }, [open, initialGraphId, tabId]);

  const activeGraph = useMemo(
    () => draft.find((g) => g.id === activeGraphId) ?? draft[0] ?? null,
    [draft, activeGraphId],
  );

  // Time-axis commits (Enter/blur) apply immediately to all graphs and skip
  // the Apply/OK/Cancel draft model — they're meant to drive the plot's
  // local-seed effect on dialog-initiated crop changes.
  const handleTimeAxisCommit = useCallback(
    (patch) => {
      if (!tabId) return;
      setTimeAxisForAllGraphs(tabId, patch);
      setDraft((prev) => prev.map((g) => ({ ...g, scaling: { ...g.scaling, ...patch } })));
      if (Number.isFinite(patch.xMin) && Number.isFinite(patch.xMax)) {
        useNavigationStore.getState().setVisibleWindow(tabId, {
          xMinSec: patch.xMin,
          xMaxSec: patch.xMax,
        });
      }
    },
    [tabId, setTimeAxisForAllGraphs],
  );

  // Data extent in seconds, used to clamp time-axis input.
  const dataExtentSec = useMemo(() => {
    const firstChamber = Object.values(tab?.chambers || {})[0];
    const ts = firstChamber?.data?.timeSeries;
    if (!Array.isArray(ts) || ts.length === 0) return null;
    return [ts[0], ts[ts.length - 1]];
  }, [tab?.chambers]);

  // Available signals = chamber-bound (O2/O2Flux) + chamberless deviceSeries.
  const availableSignals = useMemo(() => {
    if (!tab) return [];
    const sigs = [];
    for (const [chamberIdx, ch] of Object.entries(tab.chambers || {})) {
      if (ch?.data?.oxygenSeries) sigs.push({ channel: 'O2', chamberIndex: Number(chamberIdx) });
      if (ch?.data?.fluxSeries) sigs.push({ channel: 'O2Flux', chamberIndex: Number(chamberIdx) });
    }
    for (const channel of Object.keys(tab.deviceSeries || {})) {
      if (DATA_TYPE_META[channel]) {
        sigs.push({ channel, chamberIndex: null });
      }
    }
    return sigs;
  }, [tab]);

  const availableChambers = useMemo(() => {
    if (!tab) return [];
    return Object.keys(tab.chambers || {})
      .map((k) => ({ chamberIndex: Number(k) }))
      .sort((a, b) => a.chamberIndex - b.chamberIndex);
  }, [tab]);

  const patchActiveGraph = useCallback(
    (patch) => {
      setDraft((prev) => prev.map((g) => (g.id === activeGraphId ? { ...g, ...patch } : g)));
    },
    [activeGraphId],
  );

  const handleScalingChange = (patch) => {
    patchActiveGraph({ scaling: { ...activeGraph.scaling, ...patch } });
  };

  // Compute axis extent from the active graph's traces and write it into
  // graph.scaling. 5% pad mirrors ChamberPlotView's auto-frame fallback.
  const handleFitYAxis = (axis) => {
    if (!activeGraph) return;
    const axisTraces = activeGraph.traces.filter((t) => t.yAxis === axis);
    if (axisTraces.length === 0) return;
    let lo = Infinity;
    let hi = -Infinity;
    for (const trace of axisTraces) {
      let values = null;
      if (trace.channel === 'O2' || trace.channel === 'O2Flux') {
        const ch = trace.chamberIndex != null ? tab?.chambers?.[trace.chamberIndex] : null;
        const data = ch?.data;
        if (!data) continue;
        values = trace.channel === 'O2' ? data.oxygenSeries : data.fluxSeries;
      } else {
        values = tab?.deviceSeries?.[trace.channel]?.data;
      }
      if (!Array.isArray(values)) continue;
      for (let i = 0; i < values.length; i++) {
        const v = values[i];
        if (Number.isFinite(v)) {
          if (v < lo) lo = v;
          if (v > hi) hi = v;
        }
      }
    }
    if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) return;
    const pad = Math.max(1e-6, (hi - lo) * 0.05);
    const minKey = axis === 'y2' ? 'y2Min' : 'y1Min';
    const maxKey = axis === 'y2' ? 'y2Max' : 'y1Max';
    handleScalingChange({ [minKey]: lo - pad, [maxKey]: hi + pad });
  };

  const handleAdvancedChange = (patch) => {
    patchActiveGraph({ advanced: { ...activeGraph.advanced, ...patch } });
  };

  // Scaling patch for a trace's target axis. mode='add' only seeds when the
  // axis is empty; mode='edit' always resets (clears stale bounds when the
  // user swaps channel/yAxis/chamber). Falls back to plot_config bounds when
  // available, else null (auto-framing via unionExtent in baseYScale).
  const axisBoundsForTrace = (trace, currentScaling, mode = 'add') => {
    if (!trace) return {};
    const axisKey = trace.yAxis === 'y2' ? ['y2Min', 'y2Max'] : ['y1Min', 'y1Max'];
    const currentMin = currentScaling?.[axisKey[0]];
    const currentMax = currentScaling?.[axisKey[1]];
    if (mode === 'add' && currentMin != null && currentMax != null) return {};

    const pc =
      trace.chamberIndex != null
        ? tab?.chambers?.[trace.chamberIndex]?.plotConfig?.find(
            (c) => c.data_type === trace.channel,
          )
        : null;
    if (pc && typeof pc.ymin === 'number' && typeof pc.ymax === 'number') {
      return { [axisKey[0]]: pc.ymin, [axisKey[1]]: pc.ymax };
    }
    if (mode === 'edit') return { [axisKey[0]]: null, [axisKey[1]]: null };
    return {};
  };

  const handleTraceChange = (traceId, patch) => {
    const updatedTraces = activeGraph.traces.map((t) =>
      t.id === traceId ? { ...t, ...patch } : t,
    );
    const updatedTrace = updatedTraces.find((t) => t.id === traceId);

    // Only refit the destination axis bounds when the changed trace is the
    // only one occupying that axis after the change. If siblings still share
    // the axis, their bounds shouldn't be clobbered by edits to this trace.
    const isAxisRelevantChange =
      patch.channel !== undefined || patch.yAxis !== undefined || patch.chamberIndex !== undefined;
    let scalingPatch = {};
    if (isAxisRelevantChange) {
      const destAxis = updatedTrace.yAxis;
      const hasSiblingOnDest = updatedTraces.some((t) => t.id !== traceId && t.yAxis === destAxis);
      if (!hasSiblingOnDest) {
        scalingPatch = axisBoundsForTrace(updatedTrace, activeGraph.scaling, 'edit');
      }
    }

    patchActiveGraph({
      traces: updatedTraces,
      ...(Object.keys(scalingPatch).length > 0
        ? { scaling: { ...activeGraph.scaling, ...scalingPatch } }
        : {}),
    });
  };

  const handleTraceAdd = (trace) => {
    const newTrace = {
      id: `trace-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      ...trace,
    };
    const scalingPatch = axisBoundsForTrace(newTrace, activeGraph.scaling, 'add');
    patchActiveGraph({
      traces: [...activeGraph.traces, newTrace],
      selectedTraceId: activeGraph.selectedTraceId ?? newTrace.id,
      ...(Object.keys(scalingPatch).length > 0
        ? { scaling: { ...activeGraph.scaling, ...scalingPatch } }
        : {}),
    });
  };

  const handleTraceRemove = (traceId) => {
    const nextTraces = activeGraph.traces.filter((t) => t.id !== traceId);
    patchActiveGraph({
      traces: nextTraces,
      selectedTraceId:
        activeGraph.selectedTraceId === traceId
          ? (nextTraces[0]?.id ?? null)
          : activeGraph.selectedTraceId,
    });
  };

  const handleAddGraph = () => {
    const newId = `graph-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    // Seed time axis from any existing graph so the new empty graph shares
    // the current x window.
    const refScaling = draft[0]?.scaling;
    const newGraph = {
      id: newId,
      kind: 'extra',
      chamberIndex: null,
      label: `Graph ${draft.length + 1}`,
      scaling: {
        xMin: refScaling?.xMin ?? null,
        xMax: refScaling?.xMax ?? null,
        xUnit: refScaling?.xUnit ?? 'hh:mm:ss',
        y1Min: null,
        y1Max: null,
        y2Min: null,
        y2Max: null,
      },
      traces: [],
      advanced: {
        showMarks: true,
        showEvents: true,
        showInstrumentAndNotificationEvents: true,
      },
      selectedTraceId: null,
    };
    setDraft((prev) => [...prev, newGraph]);
    setActiveGraphId(newId);
    setActiveTab(1); // jump to Plots tab for newly-created empty graph
  };

  const handleDeleteGraph = () => {
    if (!activeGraph || activeGraph.kind !== 'extra') return;
    setDraft((prev) => {
      const next = prev.filter((g) => g.id !== activeGraph.id);
      setActiveGraphId(next[0]?.id ?? null);
      return next;
    });
  };

  const handleApply = () => {
    applyGraphDraft(tabId, draft);
  };

  const handleOk = () => {
    applyGraphDraft(tabId, draft);
    onClose?.();
  };

  const handleCancel = () => {
    onClose?.();
  };

  if (!tab) return null;

  const canDelete = activeGraph?.kind === 'extra';

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="lg" fullWidth>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '80vh' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1.5,
            borderBottom: '1px solid #dde3e6',
          }}
        >
          <Typography variant="h6" sx={{ flex: 1 }}>
            Graph options: {activeGraph?.label ?? '—'}
          </Typography>
          <IconButton size="small" onClick={handleCancel} aria-label="Close">
            <CloseIcon size={18} />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {/* Left menu */}
          <Box sx={{ width: 200, borderRight: '1px solid #dde3e6', overflow: 'auto' }}>
            <List dense disablePadding>
              {draft.map((g) => (
                <ListItemButton
                  key={g.id}
                  selected={g.id === activeGraphId}
                  onClick={() => setActiveGraphId(g.id)}
                >
                  <ListItemText primary={g.label} />
                </ListItemButton>
              ))}
            </List>
          </Box>

          {/* Right pane */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{ borderBottom: '1px solid #dde3e6' }}
            >
              <Tab label="Scaling" />
              <Tab label="Plots" />
              <Tab label="Advanced" />
            </Tabs>
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {activeGraph && activeTab === 0 && (
                <ScalingTab
                  graph={activeGraph}
                  onScalingChange={handleScalingChange}
                  onTimeAxisCommit={handleTimeAxisCommit}
                  onFitYAxis={handleFitYAxis}
                  dataExtentSec={dataExtentSec}
                />
              )}
              {activeGraph && activeTab === 1 && (
                <PlotsTab
                  graph={activeGraph}
                  onTraceChange={handleTraceChange}
                  onTraceAdd={handleTraceAdd}
                  onTraceRemove={handleTraceRemove}
                  availableSignals={availableSignals}
                  availableChambers={availableChambers}
                />
              )}
              {activeGraph && activeTab === 2 && (
                <AdvancedTab graph={activeGraph} onAdvancedChange={handleAdvancedChange} />
              )}
            </Box>
          </Box>
        </Box>

        <Divider />
        <Box sx={{ display: 'flex', px: 2, py: 1.5, alignItems: 'center', gap: 1 }}>
          <Button size="small" onClick={handleAddGraph}>
            Add graph
          </Button>
          <Button size="small" onClick={handleDeleteGraph} disabled={!canDelete} color="error">
            Delete graph
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={handleCancel}>
            Cancel
          </Button>
          <Button size="small" onClick={handleApply} variant="outlined">
            Apply
          </Button>
          <Button size="small" onClick={handleOk} variant="outlined">
            OK
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
